import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { geocodeAddress } from '@/lib/geocoding';

export interface CreateOrderData {
  // Itinéraire - Adresses structurées
  pickup_address?: string;
  pickup_street_number?: string;
  pickup_street_name?: string;
  pickup_postal_code?: string;
  pickup_city?: string;
  pickup_country?: string;
  pickup_lat?: number;
  pickup_lng?: number;
  pickup_date: string;
  pickup_contact: string;
  pickup_phone: string;
  start_details?: string;
  
  delivery_address?: string;
  delivery_street_number?: string;
  delivery_street_name?: string;
  delivery_postal_code?: string;
  delivery_city?: string;
  delivery_country?: string;
  delivery_lat?: number;
  delivery_lng?: number;
  delivery_date: string;
  delivery_contact: string;
  delivery_phone: string;
  delivery_details?: string;
  
  // Marchandise
  cargo_title: string;
  trailer_type: string;
  weight_kg: number;
  volume_m3?: number;
  pallets_count?: number;
  cargo_description: string;
  special_instructions?: string;
  
  // Tarification
  proposed_price?: number;
  marketplace_visible: boolean;
  status: 'draft' | 'posted';
}

export const useOrders = () => {
  const queryClient = useQueryClient();

  // Fetch shipper's orders
  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['orders'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          transport_jobs (
            id,
            driver_id,
            vehicle_id,
            booked_at,
            assigned_at,
            accepted_at,
            started_at,
            completed_at,
            companies!transporter_company_id (
              name,
              email,
              phone,
              address,
              siret
            ),
            drivers (
              license_number,
              status,
              users (
                first_name,
                last_name,
                phone
              )
            ),
            vehicles (
              plate_number,
              brand,
              model,
              year,
              vehicle_types (
                name,
                trailer_type
              )
            )
          )
        `)
        .eq('shipper_user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });

  // Create order mutation
  const createOrderMutation = useMutation({
    mutationFn: async (orderData: CreateOrderData) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get user's company_id
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('company_id')
        .eq('id', user.id)
        .single();

      if (userError || !userData?.company_id) {
        throw new Error('Could not fetch user company');
      }

      const { data, error } = await supabase
        .from('orders')
        .insert([{
          order_number: '', // Auto-generated by trigger
          pickup_address: orderData.pickup_address,
          pickup_street_number: orderData.pickup_street_number,
          pickup_street_name: orderData.pickup_street_name,
          pickup_postal_code: orderData.pickup_postal_code,
          pickup_city: orderData.pickup_city,
          pickup_country: orderData.pickup_country,
          pickup_lat: orderData.pickup_lat,
          pickup_lng: orderData.pickup_lng,
          pickup_date: orderData.pickup_date,
          pickup_contact: orderData.pickup_contact,
          pickup_phone: orderData.pickup_phone,
          start_details: orderData.start_details,
          delivery_address: orderData.delivery_address,
          delivery_street_number: orderData.delivery_street_number,
          delivery_street_name: orderData.delivery_street_name,
          delivery_postal_code: orderData.delivery_postal_code,
          delivery_city: orderData.delivery_city,
          delivery_country: orderData.delivery_country,
          delivery_lat: orderData.delivery_lat,
          delivery_lng: orderData.delivery_lng,
          delivery_date: orderData.delivery_date,
          delivery_contact: orderData.delivery_contact,
          delivery_phone: orderData.delivery_phone,
          delivery_details: orderData.delivery_details,
          cargo_title: orderData.cargo_title,
          trailer_type: orderData.trailer_type as any,
          weight_kg: orderData.weight_kg,
          volume_m3: orderData.volume_m3,
          pallets_count: orderData.pallets_count,
          cargo_description: orderData.cargo_description,
          special_instructions: orderData.special_instructions,
          proposed_price: orderData.proposed_price,
          marketplace_visible: orderData.marketplace_visible,
          status: orderData.status as any,
          shipper_user_id: user.id,
          shipper_company_id: userData.company_id,
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  // Update order mutation
  const updateOrderMutation = useMutation({
    mutationFn: async ({ id, ...orderData }: Partial<CreateOrderData> & { id: string }) => {
      const updateData: Record<string, any> = {};
      
      if (orderData.pickup_address) updateData.pickup_address = orderData.pickup_address;
      if (orderData.pickup_date) updateData.pickup_date = orderData.pickup_date;
      if (orderData.pickup_contact) updateData.pickup_contact = orderData.pickup_contact;
      if (orderData.pickup_phone) updateData.pickup_phone = orderData.pickup_phone;
      if (orderData.start_details !== undefined) updateData.start_details = orderData.start_details;
      if (orderData.delivery_address) updateData.delivery_address = orderData.delivery_address;
      if (orderData.delivery_date) updateData.delivery_date = orderData.delivery_date;
      if (orderData.delivery_contact) updateData.delivery_contact = orderData.delivery_contact;
      if (orderData.delivery_phone) updateData.delivery_phone = orderData.delivery_phone;
      if (orderData.delivery_details !== undefined) updateData.delivery_details = orderData.delivery_details;
      if (orderData.trailer_type) updateData.trailer_type = orderData.trailer_type;
      if (orderData.weight_kg) updateData.weight_kg = orderData.weight_kg;
      if (orderData.volume_m3 !== undefined) updateData.volume_m3 = orderData.volume_m3;
      if (orderData.pallets_count !== undefined) updateData.pallets_count = orderData.pallets_count;
      if (orderData.cargo_description) updateData.cargo_description = orderData.cargo_description;
      if (orderData.cargo_title) updateData.cargo_title = orderData.cargo_title;
      if (orderData.special_instructions !== undefined) updateData.special_instructions = orderData.special_instructions;
      if (orderData.proposed_price !== undefined) updateData.proposed_price = orderData.proposed_price;
      if (orderData.marketplace_visible !== undefined) updateData.marketplace_visible = orderData.marketplace_visible;
      if (orderData.status) updateData.status = orderData.status;

      const { data, error } = await supabase
        .from('orders')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });

  // Delete order mutation
  const deleteOrderMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const { error } = await supabase
        .from('orders')
        .delete()
        .eq('id', orderId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      toast.success('Commande supprimée');
    },
  });

  // Geocode existing orders mutation
  const geocodeExistingOrdersMutation = useMutation({
    mutationFn: async () => {
      const { data: ordersToGeocode, error: fetchError } = await supabase
        .from('orders')
        .select('id, pickup_address, pickup_city, pickup_country, delivery_address, delivery_city, delivery_country')
        .or('pickup_lat.is.null,delivery_lat.is.null');

      if (fetchError) throw fetchError;
      if (!ordersToGeocode || ordersToGeocode.length === 0) {
        return { geocoded: 0, total: 0 };
      }

      let successCount = 0;

      for (const order of ordersToGeocode) {
        try {
          const pickupAddress = `${order.pickup_address || ''}, ${order.pickup_city || ''}, ${order.pickup_country || ''}`.trim();
          const deliveryAddress = `${order.delivery_address || ''}, ${order.delivery_city || ''}, ${order.delivery_country || ''}`.trim();

          const [pickupResult, deliveryResult] = await Promise.all([
            geocodeAddress(pickupAddress),
            geocodeAddress(deliveryAddress)
          ]);

          if ('latitude' in pickupResult && 'latitude' in deliveryResult) {
            const { error: updateError } = await supabase
              .from('orders')
              .update({
                pickup_lat: pickupResult.latitude,
                pickup_lng: pickupResult.longitude,
                delivery_lat: deliveryResult.latitude,
                delivery_lng: deliveryResult.longitude
              })
              .eq('id', order.id);

            if (!updateError) successCount++;
          }
        } catch (error) {
          console.error(`Failed to geocode order ${order.id}:`, error);
        }
      }

      return { geocoded: successCount, total: ordersToGeocode.length };
    },
    onSuccess: (data) => {
      toast.success(`${data.geocoded} commandes sur ${data.total} géocodées avec succès.`);
      queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Erreur lors du géocodage');
    },
  });

  return {
    orders,
    isLoading,
    createOrder: createOrderMutation.mutateAsync,
    updateOrder: updateOrderMutation.mutateAsync,
    deleteOrder: deleteOrderMutation.mutateAsync,
    geocodeExistingOrders: geocodeExistingOrdersMutation.mutateAsync,
    isGeocoding: geocodeExistingOrdersMutation.isPending,
  };
};
